import { useCallback, useEffect, useRef, useState } from 'react'
import { useAction } from '../utils/react-redux'
import { setToken, setUserConnected, setUserNotConnected } from '../../store/userAccount/userAccount.actions'
import { setUserUIScreen } from '../../store/ui/ui.actions'
import { USER_SCREENS } from '../../store/ui/ui.constants'
import { pick } from '../../utils/object'
import KeycloakJs from 'keycloak-js'
import { getService } from '../../parameters'
import SERVICES from '../../services.constants'
import debug from 'debug'
import { USER_ID_LEGACY, USER_ID_SUB } from './UserAccount.constants'

const d = debug('keycloak')

// webpack:BrowserOnly

export const EXPIRATION_SECONDS = 60

let keycloak

export const setKeycloakForTestOnly = kc => {
  keycloak = kc
}

const useKeycloak = () => {
  const [loaded, setLoaded] = useState(Boolean(keycloak))

  useEffect(() => {
    if (keycloak) {
      setLoaded(true)
    } else {
      keycloak = new KeycloakJs({
        url: getService(SERVICES.KEYCLOAK).url,
        realm: 'mappy',
        clientId: 'web-login'
      })
      keycloak
        .init({
          onLoad: 'check-sso',
          responseMode: 'query',
          silentCheckSsoFallback: false,
          silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html'
        })
        .then(authenticated => {
          setLoaded(true)
          d(`User is ${authenticated ? 'authenticated' : 'not authenticated'}`)
        })
        .catch(error => {
          console.error('Failed to initialize adapter:', error)
        })
    }
  }, [])

  return { loaded, keycloak }
}

export const useKeycloakEngine = () => {
  const { loaded, keycloak } = useKeycloak()
  const timeoutRef = useRef()

  const execSetUserConnected = useAction(setUserConnected)
  const execSetUserNotConnected = useAction(setUserNotConnected)
  const execSetUserScreen = useAction(setUserUIScreen)
  const execSetToken = useAction(setToken)

  useEffect(() => {
    if (loaded) {
      if (keycloak?.authenticated === true) {
        const token = keycloak.token
        d('keycloak.idTokenParsed', keycloak.idTokenParsed)
        const identity = pick(keycloak.idTokenParsed, ['name', 'picture', 'email', USER_ID_LEGACY, USER_ID_SUB])
        d('connected, token', token, 'idTokenParsed', keycloak.idTokenParsed)
        execSetUserConnected({ token, identity })
      } else if (keycloak.authenticated === false) {
        d('not connected')
        execSetUserNotConnected()
      }
      execSetUserScreen(USER_SCREENS.default)
    }
  }, [loaded, keycloak?.authenticated])

  const updateAndSaveToken = useCallback(
    () =>
      loaded &&
      keycloak
        .updateToken(EXPIRATION_SECONDS)
        .then(hasTokenBeenRefreshed => {
          d('updateToken - hasTokenBeenRefreshed', hasTokenBeenRefreshed, 'new token', keycloak.token)
          execSetToken(keycloak.token)
        })
        .catch(e => {
          d('updateToken failed - disconnect user', e)
          execSetUserNotConnected()
        }),
    [loaded, keycloak]
  )

  useEffect(() => {
    const checkIfTokenWillExpire = () => {
      if (loaded && keycloak?.authenticated) {
        d('checkIfTokenWillExpire')
        if (keycloak.isTokenExpired(EXPIRATION_SECONDS)) {
          d('isTokenExpired:true')
          updateAndSaveToken()
        }
        timeoutRef.current = setTimeout(checkIfTokenWillExpire, EXPIRATION_SECONDS * 1000)
      }
    }
    if (loaded && keycloak?.authenticated) {
      timeoutRef.current = setTimeout(checkIfTokenWillExpire, EXPIRATION_SECONDS * 1000)
    }
    return () => {
      clearTimeout(timeoutRef)
    }
  }, [loaded, keycloak?.authenticated, timeoutRef])

  useEffect(() => {
    if (loaded && keycloak) {
      keycloak.onTokenExpired = () => {
        d('token expired')
        updateAndSaveToken()
      }
    }
  }, [loaded, keycloak])
}

export default useKeycloak
